/*
 * marking-check/app.js — страница /marking-check: ввод (название, код ТН ВЭД,
 * ОКПД 2, штрихкод, код маркировки, камера, фото), ответ на один экран,
 * уточняющие вопросы, дерево ТН ВЭД, план по роли, календарь ICS, PDF.
 *
 * Данные — из страницы (#mc-data, window.__MC); дерево ТН ВЭД (2 МБ, сжатое
 * ≈ 0,4 МБ) грузится лениво: при первом фокусе в поле, при коде из ссылки или
 * по «Сканировать». Без дерева проверка маркировки по коду работает — дерево
 * нужно только для названий и поиска по словам.
 */
import * as E from './engine.js';
import * as T from './tnved.js';

const W = window.__MC || {};
const L = W.i18n || {};
const SLUG = 'marking-check';
const $ = (id) => document.getElementById(id);
const RAW = JSON.parse(($('mc-data') || {}).textContent || '{}');
const M = E.buildModel({ perechen: RAW.perechen, groups: RAW.groups });
const SYN = RAW.synonyms || {};

const S = {
    query: '', kind: 'empty', code: '', okpd: '', group: '', answers: {}, role: 'producer', today: W.today || E.isoToday(),
    result: null, lookup: null,
};
let IDX = null, treeP = null;

/* ── мелочи ─────────────────────────────────────────────────────────────── */

function tr(key, vars) {
    let s = L[key];
    if (s === undefined) s = key;
    // Длинные ключи первыми: иначе «:n» съедает начало «:name».
    if (vars) for (const k of Object.keys(vars).sort((a, b) => b.length - a.length)) s = s.split(':' + k).join(vars[k] == null ? '' : String(vars[k]));
    return s;
}
function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
const LOC = (window.__intlLocale && window.__intlLocale()) || window.__LOCALE || 'ru';
let dateFmt = null;
try { if (!/^ru/.test(LOC)) dateFmt = new Intl.DateTimeFormat(LOC, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }); } catch (e) { /* old engine */ }
/** Дата для человека: 01.11.2026 по-русски, «Nov 1, 2026» по-английски (02.11 и 11.02 там читают по-разному). */
function dmy(iso) {
    if (!iso) return '—';
    const [y, m, d] = iso.split('-');
    if (dateFmt) { try { return dateFmt.format(new Date(Date.UTC(+y, +m - 1, +d))); } catch (e) { /* fall through */ } }
    return d + '.' + m + '.' + y;
}
/** Редакция пункта: «249-р+956-р» → «249-р и 956-р»; по-английски — «249-r and 956-r». */
function docLabel(doc) {
    const parts = String(doc || '').split('+').filter(Boolean);
    return (/^ru/.test(LOC) ? parts : parts.map((x) => x.replace(/-р$/, '-r'))).join(L.and_word ? ' ' + L.and_word + ' ' : ', ');
}
/** «2 группы», «21 группу», «5 групп» — все числа со словом идут через E.plural. */
function pl(n, key) { return E.plural(n, L['pl_' + key], LOC); }
function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (e) { /* optional */ } }
function feature(f, p) { try { if (window.__aeFeature) window.__aeFeature(f, p || {}); } catch (e) { /* optional */ } }
/* Воронка: ready → query (первый ввод, скан, фото) → complete (ответ, __aeToolComplete)
   → refined (ответ на уточнение) / exported (PDF, календарь, ссылка). Каждая стадия —
   один раз за смену, повтор той же стадии телеметрия сама схлопывает. */
function stage(n, m) { try { if (window.__stage) window.__stage(n, m || {}); } catch (e) { /* optional */ } }
function diag(issue, m) { try { if (window.__aeDiagnostic) window.__aeDiagnostic(Object.assign({ issue }, m || {})); } catch (e) { /* optional */ } }
const IS_RU = /^ru/.test(String(window.__LOCALE || 'ru'));
function stageText(k) { return (W.stage || {})[k] || k; }
function evText(k) { return (W.ev || {})[k] || k; }

/* ── дерево ТН ВЭД ──────────────────────────────────────────────────────── */

function loadTree() {
    if (treeP) return treeP;
    const url = (W.tnved && W.tnved.treeUrl) || '/data/tnved/tree.json';
    treeP = fetch(url, { credentials: 'same-origin' })
        .then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then((tree) => { IDX = T.indexTree(tree); feature('mc_tree_loaded', { n: tree.length }); return IDX; })
        .catch((err) => {
            treeP = null;
            diag('mc_tree_failed', { err: String(err && err.message || err).slice(0, 80) });
            throw err;
        });
    return treeP;
}

/* ── ввод и подсказки ───────────────────────────────────────────────────── */

const qEl = $('mc-q'), menu = $('mc-menu');
// menuFor — запрос, на который отвечает открытый список. Enter сразу после
// вставки кода (140 мс до разбора) выбирал первую строку СТАРОГО списка:
// «14.13.21» + Enter давал «БАД» от прошлого «бад» (QA 23.10.2026).
let menuItems = [], menuCur = -1, typeTimer = 0, menuFor = null, queried = false;

function onInput() {
    clearTimeout(typeTimer);
    typeTimer = setTimeout(() => handleQuery(qEl.value, { typing: true }), 140);
}

function handleQuery(raw, opts) {
    const o = opts || {};
    const c = E.classifyQuery(raw);
    S.query = raw; S.kind = c.kind;
    $('mc-scanned').hidden = true;
    if (c.kind === 'empty') { closeMenu(); return; }
    if (!queried) { queried = true; stage('query', { kind: c.kind, via: o.from || 'typed' }); }
    if (c.kind === 'tnved') {
        closeMenu();
        if (c.value.length >= 4) setCode(c.value, { from: o.from || 'typed' });
        return;
    }
    if (c.kind === 'okpd2') { closeMenu(); setOkpdQuery(c.value); return; }
    if (c.kind === 'gtin' || c.kind === 'mark') {
        closeMenu();
        if (c.kind === 'gtin' && !E.gtinValid(c.value)) {
            // Набранные 12–14 цифр с неверной контрольной — опечатка, а не товар:
            // молчание выглядело как «тул сломан», запрос в базы — как «товара нет».
            const box = $('mc-scanned');
            box.hidden = false;
            box.textContent = tr('gtin_bad', { gtin: c.value });
            if (!o.typing) feature('mc_gtin_bad', { len: c.value.length });
            return;
        }
        onProductCode(c, o.from || 'typed');
        return;
    }
    if (String(raw).trim().length < 2) { closeMenu(); return; }
    suggest(raw);
}

function suggest(q) {
    const list = [];
    // Пункты перечня и группы по названию — это и есть ответ, если человек
    // пишет «молочная продукция», а не «сыр».
    const qs = T.norm(q).split(' ').filter((w) => w.length > 2).map(T.stem);
    if (qs.length) {
        for (const g of M.groups.values()) {
            const words = T.norm(g.name).split(' ').map(T.stem);
            if (qs.every((x) => words.some((w) => T.wordHit(w, x)))) list.push({ type: 'group', g });
        }
    }
    if (!IDX) {
        renderMenu(list, true);
        loadTree().then(() => { if (qEl.value === q) suggest(q); }).catch(() => renderMenu(list, false, true));
        return;
    }
    for (const r of T.search(IDX, q, { synonyms: SYN, limit: 12 })) list.push({ type: 'code', r });
    renderMenu(list, false, false, q);
}

function badgeFor(code) {
    const ev = E.evaluate(M, E.matchTnved(M, code), { today: S.today, code });
    if (ev.verdict === 'yes' || ev.verdict === 'yes_multi') return '<span class="mc-badge mc-b-mandatory">' + esc(tr('menu_marked')) + '</span>';
    if (ev.verdict === 'soon') {
        const l = ev.lines.find((x) => x.status === 'required');
        return '<span class="mc-badge mc-b-upcoming">' + esc(tr('menu_soon', { date: dmy(l && l.stage.since) })) + '</span>';
    }
    if (ev.verdict === 'experiment') return '<span class="mc-badge mc-b-experiment">' + esc(tr('menu_exp')) + '</span>';
    if (ev.verdict === 'depends') return '<span class="mc-badge mc-b-experiment">' + esc(tr('menu_part')) + '</span>';
    return '<span class="mc-badge mc-b-ok">' + esc(tr('menu_no')) + '</span>';
}

function renderMenu(list, loading, failed, q) {
    menuItems = list; menuCur = -1; menuFor = q == null ? qEl.value : q;
    let html = '';
    list.forEach((it, i) => {
        if (it.type === 'group') {
            html += '<button type="button" class="tc-opt mc-opt" role="option" id="mc-opt-' + i + '" data-i="' + i + '"><span class="mc-opt-top">'
                + '<span class="mc-opt-name">🏷️ ' + esc(it.g.name) + '</span><span class="mc-opt-badge"><span class="mc-badge mc-b-' + (it.g.kind === 'experiment' ? 'experiment' : 'mandatory') + '">' + esc(tr('menu_group')) + '</span></span></span></button>';
        } else {
            const e = IDX.tree[it.r.i];
            const ctx = T.chainOf(IDX, it.r.i).slice(0, -1).map((j) => IDX.tree[j][2]).join(' → ');
            html += '<button type="button" class="tc-opt mc-opt" role="option" id="mc-opt-' + i + '" data-i="' + i + '"><span class="mc-opt-top">'
                + '<span class="mc-opt-code">' + esc(T.fmtCode(e[0])) + '</span><span class="mc-opt-name">' + esc(e[2]) + '</span>'
                + '<span class="mc-opt-badge">' + badgeFor(e[0]) + '</span></span>'
                + (ctx ? '<span class="mc-opt-ctx">' + esc(ctx) + '</span>' : '') + '</button>';
        }
    });
    if (loading) html += '<div class="tc-empty">' + esc(tr('loading_tree')) + '</div>';
    else if (failed) html += '<div class="tc-empty">' + esc(tr('tree_failed')) + '</div>';
    else if (!list.length) html += '<div class="tc-empty">' + esc(tr('menu_none')) + '</div>';
    menu.innerHTML = html;
    menu.hidden = false;
    qEl.setAttribute('aria-expanded', 'true');
}

function closeMenu() { menu.hidden = true; qEl.setAttribute('aria-expanded', 'false'); qEl.removeAttribute('aria-activedescendant'); menuCur = -1; }

function pick(i) {
    const it = menuItems[i];
    if (!it) return;
    closeMenu();
    if (it.type === 'group') { showGroup(it.g.id, 'menu'); return; }
    const code = IDX.tree[it.r.i][0];
    feature('mc_pick', { via: it.r.via, len: code.length });
    setCode(code, { from: 'search' });
}

menu.addEventListener('mousedown', (e) => e.preventDefault());
menu.addEventListener('click', (e) => { const b = e.target.closest('[data-i]'); if (b) pick(+b.dataset.i); });
qEl.addEventListener('input', onInput);
qEl.addEventListener('focus', () => { loadTree().catch(() => {}); if (menuItems.length && S.kind === 'text' && qEl.value.trim().length > 1) menu.hidden = false; });
qEl.addEventListener('blur', () => setTimeout(closeMenu, 120));
qEl.addEventListener('keydown', (e) => {
    const opts = menu.hidden ? [] : [...menu.querySelectorAll('[data-i]')];
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!opts.length) return;
        e.preventDefault();
        menuCur = (menuCur + (e.key === 'ArrowDown' ? 1 : -1) + opts.length) % opts.length;
        opts.forEach((o, i) => { o.classList.toggle('cur', i === menuCur); o.setAttribute('aria-selected', i === menuCur ? 'true' : 'false'); });
        qEl.setAttribute('aria-activedescendant', opts[menuCur].id);
        opts[menuCur].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter') {
        e.preventDefault();
        if (opts.length && menuFor === qEl.value) pick(+(opts[Math.max(0, menuCur)].dataset.i));
        else { clearTimeout(typeTimer); handleQuery(qEl.value, { from: 'enter' }); }
    } else if (e.key === 'Escape') closeMenu();
});
$('mc-examples').addEventListener('click', (e) => {
    const b = e.target.closest('[data-q]');
    if (!b) return;
    qEl.value = b.dataset.q;
    feature('mc_example', { q: b.dataset.q });
    loadTree().catch(() => {});
    handleQuery(b.dataset.q, { from: 'example' });
    if (E.classifyQuery(b.dataset.q).kind === 'text') qEl.focus();
});

/* ── код → ответ ────────────────────────────────────────────────────────── */

function setCode(code, opts) {
    S.code = E.digits(code); S.group = ''; S.answers = {};
    S.kindView = 'code';
    if (opts && opts.from !== 'typed' && opts.from !== 'url') qEl.value = T.fmtCode(S.code);
    compute();
    saveUrl();
    feature('mc_check', { len: S.code.length, from: (opts && opts.from) || '', v: S.result.verdict });
    try { if (window.__aeToolComplete) window.__aeToolComplete({ v: S.result.verdict, len: S.code.length }); } catch (e) { /* optional */ }
    if (!IDX) loadTree().then(() => renderCode()).catch(() => renderCode());
}

function setOkpdQuery(okpd) {
    S.code = ''; S.group = ''; S.answers = {}; S.okpd = okpd; S.kindView = 'okpd';
    $('mc-okpd').value = okpd;
    compute();
    saveUrl();
    feature('mc_check_okpd', { v: S.result.verdict });
}

function showGroup(id, from) {
    const g = M.groups.get(id);
    if (!g) return;
    S.code = ''; S.group = id; S.answers = {}; S.kindView = 'group';
    qEl.value = g.name;
    compute();
    saveUrl();
    feature('mc_group', { g: id, from: from || '' });
    const panel = $('mc-answer-panel');
    if (panel && panel.scrollIntoView) panel.scrollIntoView({ block: 'nearest' });
}

function compute() {
    let found;
    if (S.kindView === 'group') {
        const g = M.groups.get(S.group);
        const hits = [];
        for (const it of g.itemObjs) it.rows.forEach((r, ri) => hits.push({ p: '', item: it.n, group: g.id, row: ri, kind: 'in' }));
        if (!g.itemObjs.length) hits.push({ p: (g.codes && g.codes[0] && g.codes[0].p) || '', item: null, group: g.id, row: -1, kind: 'in' });
        found = { hits, excluded: [] };
    } else if (S.kindView === 'okpd') {
        found = E.matchOkpd2(M, S.okpd);
    } else {
        found = E.matchTnved(M, S.code);
    }
    S.result = E.evaluate(M, found, { today: S.today, answers: S.answers, okpd2: S.kindView === 'code' ? S.okpd : '', code: S.code });
    render();
}

function render() {
    renderAnswer();
    renderQuestions();
    renderCode();
    renderPlan();
    renderCalendar();
    renderExceptions();
}

function groupLines() {
    const map = new Map();
    for (const l of S.result.lines) {
        if (!map.has(l.group.id)) map.set(l.group.id, []);
        map.get(l.group.id).push(l);
    }
    return [...map.entries()];
}

function renderAnswer() {
    const r = S.result;
    const v = $('mc-answer-v'), sub = $('mc-answer-sub');
    const req = r.lines.filter((l) => l.status === 'required');
    const first = req[0] || r.lines[0];
    const codeLabel = S.kindView === 'okpd' ? S.okpd : T.fmtCode(S.code);
    v.className = 'tc-answer-v mc-verdict ' + ({ yes: 'yes', yes_multi: 'yes', soon: 'soon', experiment: 'experiment', depends: 'depends', exempt: 'exempt', no: 'no' }[r.verdict] || '');
    v.textContent = tr('v_' + r.verdict, { date: first ? dmy(first.stage.since) : '' });
    const gs = new Set(req.map((l) => l.group.id));
    const why = r.lines.filter((l) => l.why).map((l) => (W.qs[l.why.q] || {}).short || l.why.q);
    sub.textContent = tr('sub_' + r.verdict, {
        group: first ? first.group.name : '', date: first ? dmy(first.stage.since) : '', n: gs.size, groups: pl(gs.size, 'groups_acc'), code: codeLabel,
        until: first ? dmy(first.stage.until) : '', why: [...new Set(why)].join('; '),
    });
    $('mc-answer-l').textContent = S.kindView === 'group' ? tr('ans_label_group') : tr('ans_label_code', { code: codeLabel });
    $('mc-live').hidden = false;
    $('mc-live-v').textContent = v.textContent;
    $('mc-live-v').className = 'mc-live-v ' + v.className.replace(/tc-answer-v|mc-verdict/g, '').trim();

    let html = '';
    for (const [gid, lines] of groupLines()) {
        const g = lines[0].group;
        const live = lines.filter((l) => l.status === 'required');
        const best = live[0] || lines[0];
        const st = best.stage;
        const dim = !live.length;
        const badge = dim ? statusBadge(best.status) : '<span class="mc-badge mc-b-' + st.stage + '">' + esc(stageText(st.stage)) + '</span>';
        html += '<div class="mc-g' + (dim ? ' dim' : '') + '"><div class="mc-g-head"><p class="mc-g-name">' + esc(g.name) + '</p>' + badge + '</div>';
        const flags = [];
        if (!dim) {
            if (st.stage === 'experiment') flags.push(tr('f_exp', { date: dmy(st.until) }));
            for (const f of st.flags) {
                if (f.t === 'ban') flags.push(tr('f_ban', { date: dmy(f.e.d) }) + (f.e.scope ? ' (' + f.e.scope + ')' : ''));
                if (f.t === 'permit') flags.push(tr('f_permit', { date: dmy(f.e.d) }));
                if (f.t === 'remains') flags.push(tr('f_remains', { date: dmy(f.e.end) }) + (f.e.scope ? ' (' + f.e.scope + ')' : ''));
                if (f.t === 'exp') flags.push(tr('f_exp', { date: dmy(f.e.end) }));
            }
        }
        if (flags.length) html += '<div class="mc-flags">' + flags.map((f) => '<span class="mc-flag">' + esc(f) + '</span>').join('') + '</div>';
        const items = lines.filter((l) => l.item);
        if (items.length) {
            html += '<ul class="mc-items">' + items.slice(0, 8).map((l) => {
                const row = l.row;
                const dt = row && row.date ? dmy(row.date) : (row ? row.date_text : '');
                return '<li>' + esc(tr('item_line', { n: l.item.n, name: l.item.name, date: dt }))
                    + (l.status !== 'required' ? ' — <b>' + esc(tr('st_' + l.status)) + '</b>' : '')
                    + (l.hit && l.hit.q ? '<span class="mc-sub"> ' + esc(tr('qualifier', { q: l.hit.q })) + '</span>' : '') + '</li>';
            }).join('') + (items.length > 8 ? '<li>' + esc(tr('more_items', { n: items.length - 8, items: pl(items.length - 8, 'items') })) + '</li>' : '') + '</ul>';
        }
        if (!dim && st.next.length) {
            html += '<p class="mc-next">' + esc(tr('next_title')) + ' ' + st.next.slice(0, 3).map((e) => '<b>' + esc(dmy(e.d)) + '</b> — ' + esc(evText(e.t)) + (e.scope ? ' (' + esc(e.scope) + ')' : '')).join('; ') + '</p>';
        }
        if (g.note) html += '<p class="mc-why">' + esc(g.note) + '</p>';
        html += '<p class="mc-why">' + esc(tr('basis', { rules: g.rules })) + (best.item ? ' · ' + esc(tr('basis_item', { n: best.item.n, doc: docLabel(best.item.doc) })) : '') + '</p>';
        html += '</div>';
    }
    if (!r.lines.length && S.kindView === 'code' && S.code) {
        html += '<div class="tc-msg">' + esc(tr('no_hint')) + '</div>';
    }
    if (r.excluded.length) {
        const names = [...new Set(r.excluded.map((e) => (M.groups.get(e.group) || {}).name))].filter(Boolean);
        html += '<div class="tc-msg">' + esc(tr('excluded_note', { groups: names.join(', ') })) + '</div>';
    }
    if (S.kindView === 'code' && S.okpd && r.lines.some((l) => l.status === 'okpd_mismatch')) {
        html += '<div class="tc-msg warn">' + esc(tr('okpd_mismatch_note', { okpd: S.okpd })) + '</div>';
    } else if (S.kindView === 'code' && !S.okpd && r.lines.some((l) => l.status === 'required' && l.item)) {
        html += '<div class="tc-msg">' + esc(tr('okpd_reminder')) + '</div>';
    }
    $('mc-groups').innerHTML = html;
    $('mc-actions').hidden = !(S.code || S.group || S.okpd);
    // Не маркируется — нечего планировать и нечего класть в календарь.
    const anyLive = r.lines.some((l) => l.status === 'required');
    $('mc-plan').hidden = !anyLive;
    $('mc-cal').hidden = !anyLive;
    $('mc-ics').hidden = !anyLive;
}

function statusBadge(st) {
    return '<span class="mc-badge mc-b-' + (st === 'part' ? 'experiment' : st === 'okpd_mismatch' ? 'na' : st) + '">' + esc(tr('st_' + st)) + '</span>';
}

/* ── уточняющие вопросы ─────────────────────────────────────────────────── */

function renderQuestions() {
    const ids = S.result.questions;
    const panel = $('mc-qpanel');
    if (!ids.length) { panel.hidden = true; return; }
    panel.hidden = false;
    let html = '';
    for (const id of ids) {
        const q = M.questions[id], tq = (W.qs || {})[id] || {};
        const a = S.answers[id];
        if (q.kind === 'number') {
            html += '<div class="mc-qrow"><p class="mc-qtext">' + esc(tq.q || id) + '</p><div class="mc-qbtns">'
                + '<input class="tc-in" data-qn="' + id + '" inputmode="decimal" autocomplete="off" placeholder="' + esc(tr('net_ph')) + '" value="' + esc(a || '') + '">'
                + '<span class="mc-sub">' + esc(tq.hint || '') + '</span></div></div>';
        } else {
            const b = (val, label) => '<button type="button" class="tc-chip' + (a === val ? ' on' : '') + '" aria-pressed="' + (a === val ? 'true' : 'false') + '" data-q="' + id + '" data-a="' + (val === true ? '1' : val === false ? '0' : '') + '">' + esc(label) + '</button>';
            html += '<div class="mc-qrow"><p class="mc-qtext">' + esc(tq.q || id) + '</p>'
                + (tq.hint ? '<p class="tc-hint">' + esc(tq.hint) + '</p>' : '')
                + '<div class="mc-qbtns">' + b(true, tr('a_yes')) + b(false, tr('a_no')) + b(undefined, tr('a_unknown')) + '</div></div>';
        }
    }
    $('mc-qs').innerHTML = html;
}

$('mc-qs').addEventListener('click', (e) => {
    const b = e.target.closest('[data-q]');
    if (!b) return;
    const v = b.dataset.a === '1' ? true : b.dataset.a === '0' ? false : undefined;
    if (v === undefined) delete S.answers[b.dataset.q]; else S.answers[b.dataset.q] = v;
    feature('mc_answer', { q: b.dataset.q, a: v === undefined ? 'unknown' : v ? 'yes' : 'no' });
    compute();
    stage('refined', { v: S.result.verdict });
    saveUrl();
});
$('mc-qs').addEventListener('change', (e) => {
    const inp = e.target.closest('[data-qn]');
    if (!inp) return;
    const v = inp.value.trim().replace(',', '.');
    if (v && !isFinite(Number(v))) { toast(tr('net_bad'), 'warning'); return; }
    if (v) S.answers[inp.dataset.qn] = v; else delete S.answers[inp.dataset.qn];
    feature('mc_answer', { q: inp.dataset.qn, a: v ? 'num' : 'empty' });
    compute();
    saveUrl();
});

/* ── дерево кода ────────────────────────────────────────────────────────── */

function renderCode() {
    const panel = $('mc-code-panel');
    if (S.kindView !== 'code' || !S.code) { panel.hidden = true; return; }
    panel.hidden = false;
    const chainEl = $('mc-chain'), kids = $('mc-children');
    if (!IDX) {
        chainEl.innerHTML = '<li>' + esc(treeP ? tr('loading_tree') : tr('tree_failed')) + '</li>';
        kids.innerHTML = '';
        return;
    }
    const L0 = T.lookup(IDX, S.code);
    S.lookup = L0;
    if (!L0 || !L0.entry) {
        chainEl.innerHTML = '<li>' + esc(tr('code_unknown', { code: T.fmtCode(S.code) })) + '</li>';
        kids.innerHTML = '';
        return;
    }
    const grp = (W.tnved && W.tnved.groups || []).find((x) => x.g === S.code.slice(0, 2));
    let html = grp ? '<li><span class="mc-c">' + esc(tr('group_word', { g: grp.g })) + '</span><span>' + esc(grp.title) + '</span></li>' : '';
    L0.chain.forEach((e, i) => {
        const cur = i === L0.chain.length - 1;
        html += '<li' + (cur ? ' class="cur"' : '') + '><span class="mc-c">' + esc(e[0] ? T.fmtCode(e[0]) : '') + '</span><span>'
            + (e[1] > 0 ? '<span class="mc-dash">' + '– '.repeat(e[1]) + '</span>' : '') + esc(e[2]) + '</span></li>';
    });
    if (!L0.exact) html += '<li class="cur"><span class="mc-c">' + esc(T.fmtCode(S.code)) + '</span><span>' + esc(tr('code_not_in_edition')) + '</span></li>';
    chainEl.innerHTML = html;
    const leafLevel = S.code.length === 10 && L0.exact;
    if (!leafLevel && L0.children.length) {
        kids.innerHTML = '<p class="tc-hint">' + esc(tr('children_hint')) + '</p>'
            + L0.children.slice(0, 60).map((e) => e[0]
                ? '<button type="button" class="mc-child" data-code="' + e[0] + '"><span class="mc-c">' + esc(T.fmtCode(e[0])) + '</span><span>' + esc(e[2]) + ' ' + badgeFor(e[0]) + '</span></button>'
                : '<div class="mc-sub" style="padding:4px 8px">' + esc(e[2]) + '</div>').join('');
    } else kids.innerHTML = '';
    $('mc-notes-title').textContent = tr('notes_title_g', { g: S.code.slice(0, 2) });
    const nb = $('mc-notes-body');
    if (nb.dataset.g !== S.code.slice(0, 2)) { nb.dataset.g = ''; nb.innerHTML = '<p class="tc-hint">' + esc(tr('notes_loading')) + '</p>'; if ($('mc-notes').open) loadNotes(); }
}

$('mc-children').addEventListener('click', (e) => {
    const b = e.target.closest('[data-code]');
    if (b) { setCode(b.dataset.code, { from: 'tree' }); feature('mc_tree_child', {}); }
});
$('mc-chain').addEventListener('click', () => {});
$('mc-notes').addEventListener('toggle', () => { if ($('mc-notes').open) loadNotes(); });

function loadNotes() {
    const g = S.code.slice(0, 2);
    const nb = $('mc-notes-body');
    if (!g || nb.dataset.g === g) return;
    nb.dataset.g = g;
    fetch(((W.tnved && W.tnved.notesUrl) || '/data/tnved/notes/') + g + '.json', { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((j) => {
            if (nb.dataset.g !== g) return;
            const paras = (j.notes || []).filter((p) => !/^(РАЗДЕЛ|ГРУППА)\s/.test(p));
            nb.innerHTML = paras.length
                ? paras.map((p) => '<p class="tc-note">' + esc(p) + '</p>').join('') + '<p class="tc-attrib"><a href="' + esc(j.source) + '" rel="noopener" target="_blank">' + esc(tr('notes_src', { ed: j.edition || '—' })) + '</a></p>'
                : '<p class="tc-hint">' + esc(tr('notes_none')) + '</p>';
        })
        .catch(() => { nb.dataset.g = ''; nb.innerHTML = '<p class="tc-hint">' + esc(tr('notes_failed')) + '</p>'; });
}

/* ── план по роли ───────────────────────────────────────────────────────── */

const GROUP_PSN_BAN = ['footwear', 'fur', 'medicines'];

function planSteps(role) {
    const r = S.result;
    const live = r ? r.lines.filter((l) => l.status === 'required') : [];
    const steps = (W.steps || {})[role] || [];
    const g = live[0] && live[0].group;
    const st = live[0] && live[0].stage;
    const exp = live.length && live.every((l) => l.stage.stage === 'experiment');
    const permit = st && st.flags.find((f) => f.t === 'permit');
    const vars = { group: g ? g.name : '', date: st ? dmy(st.since) : '', permit: permit ? dmy(permit.e.d) : '' };
    const out = [];
    for (const s of steps) {
        if (s.if === 'permit' && !permit) continue;
        if (s.if === 'no_permit' && permit) continue;
        if (s.if === 'exp' && !exp) continue;
        if (s.if === 'psn_ban' && !(g && GROUP_PSN_BAN.includes(g.id))) continue;
        if (s.if === 'psn_ok' && g && GROUP_PSN_BAN.includes(g.id)) continue;
        out.push({ t: tr2(s.t, vars), d: s.d ? tr2(s.d, vars) : '' });
    }
    return out;
}
function tr2(s, vars) { let x = String(s); for (const k of Object.keys(vars).sort((a, b) => b.length - a.length)) x = x.split(':' + k).join(vars[k]); return x; }

function renderPlan() {
    const steps = planSteps(S.role);
    $('mc-steps').innerHTML = steps.map((s) => '<li>' + esc(s.t) + (s.d ? '<span class="mc-sub">' + esc(s.d) + '</span>' : '') + '</li>').join('');
    $('mc-plan-sub').textContent = (W.roles || {})[S.role] || '';
    renderCost();
}

$('mc-roles').addEventListener('click', (e) => {
    const b = e.target.closest('[data-role]');
    if (!b) return;
    S.role = b.dataset.role;
    [...$('mc-roles').children].forEach((x) => { const on = x === b; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'role', S.role); } catch (err) { /* storage off */ }
    feature('mc_role', { role: S.role });
    renderPlan();
});

function renderCost() {
    const n = $('mc-units').value.trim();
    if (!n) { $('mc-cost-out').textContent = tr('cost_hint'); return; }
    const c = E.codeCost(n);
    const nf = new Intl.NumberFormat((window.__intlLocale && window.__intlLocale()) || 'ru', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    $('mc-cost-out').textContent = tr('cost_out', { unit: nf.format(c.unit), month: nf.format(c.month), year: nf.format(c.year) });
}
$('mc-units').addEventListener('input', renderCost);

/* ── календарь и ICS ────────────────────────────────────────────────────── */

function calEvents() {
    const out = [];
    const seen = new Set();
    for (const [, lines] of groupLines()) {
        const live = lines.filter((l) => l.status === 'required');
        if (!live.length) continue;
        const g = live[0].group;
        const code = S.kindView === 'code' ? S.code : '';
        for (const e of g.events || []) {
            if (!live.some((l) => E.eventApplies(e, l.item ? l.item.n : '', code))) continue;
            const d = e.t === 'remains' && e.end ? e.end : e.d;
            if (!d) continue;
            const t = e.t === 'remains' ? 'remains_end' : e.t === 'exp' ? 'exp' : e.t;
            const k = g.id + d + t + (e.scope || '');
            if (seen.has(k)) continue;
            seen.add(k);
            out.push({ d, t, scope: e.scope || '', g });
            if (e.t === 'exp' && e.end) out.push({ d: e.end, t: 'exp_end', scope: '', g });
        }
        for (const l of live) {
            if (l.row && l.row.date && !(g.events || []).some((e) => e.t === 'start' && e.d === l.row.date)) {
                const k = g.id + l.row.date + 'start' + l.item.n;
                if (!seen.has(k)) { seen.add(k); out.push({ d: l.row.date, t: 'list_date', scope: tr('item_short', { n: l.item.n }), g }); }
            }
        }
    }
    out.sort((a, b) => (a.d < b.d ? -1 : a.d > b.d ? 1 : 0));
    return out;
}

function renderCalendar() {
    const ev = calEvents();
    const body = $('mc-cal-body');
    if (!ev.length) { body.innerHTML = '<p class="tc-hint">' + esc(tr('cal_none')) + '</p>'; return; }
    body.innerHTML = '<ul class="mc-cal-list">' + ev.map((e) => '<li class="' + (e.d < S.today ? 'past' : '') + '"><span class="mc-d">' + esc(dmy(e.d)) + '</span><span>'
        + esc(evText(e.t)) + (e.scope ? ': ' + esc(e.scope) : '') + (groupLines().length > 1 ? '<span class="mc-sub"> · ' + esc(e.g.name) + '</span>' : '') + '</span></li>').join('') + '</ul>'
        + '<div class="tc-actions mc-mt10"><button type="button" class="tc-btn sm ghost" data-ics="1">📅 ' + esc(tr('ics_future')) + '</button></div>';
}
$('mc-cal-body').addEventListener('click', (e) => { if (e.target.closest('[data-ics]')) downloadIcs(); });

function downloadIcs() {
    const ev = calEvents().filter((e) => e.d >= S.today);
    if (!ev.length) { toast(tr('ics_none'), 'info'); return; }
    const url = W.pageUrl || location.origin + location.pathname;
    const ics = E.buildIcs(ev.map((e) => ({
        d: e.d,
        title: tr('ics_title', { group: e.g.name, what: evText(e.t) }),
        text: (e.scope ? e.scope + '. ' : '') + tr('ics_text', { rules: e.g.rules, date: dmy(W.checked) }),
        url,
    })), { name: tr('ics_cal_name') });
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const name = 'marking-' + (S.code || S.group || 'dates') + '.ics';
    if (window.__downloadFile) window.__downloadFile(blob, name);
    feature('mc_ics', { n: ev.length });
    stage('exported', { what: 'ics' });
}
$('mc-ics').addEventListener('click', downloadIcs);

/* ── исключения дословно ────────────────────────────────────────────────── */

function renderExceptions() {
    const body = $('mc-exc-body');
    const seen = new Set();
    let html = '';
    for (const l of S.result.lines) {
        if (!l.item) continue;
        for (const r of l.item.rows) {
            const k = l.item.n + '|' + r.note;
            if (seen.has(k)) continue;
            seen.add(k);
            const codes = r.tnved.map((c) => T.fmtCode(c.p) + (c.x ? ' (' + tr('except_word') + ' ' + c.x.map(T.fmtCode).join(', ') + ')' : '') + (c.q && !c.x ? ' (' + c.q + ')' : '')).join(', ');
            html += '<div class="mc-exc-item"><h4>' + esc(tr('exc_item', { n: l.item.n, name: l.item.name, date: r.date_text })) + '</h4>'
                + (r.note ? '<p>' + esc(r.note) + '</p>' : '<p class="mc-sub">' + esc(tr('exc_no_note')) + '</p>')
                + '<p class="mc-exc-codes">' + esc(tr('exc_codes', { tnved: codes || '—', okpd: (r.okpd2 || []).join(', ') || '—' })) + '</p></div>';
        }
    }
    if (html && M.footnote) html += '<p class="tc-note">' + esc(tr('footnote', { text: M.footnote })) + '</p>';
    body.innerHTML = html || '<p class="tc-hint">' + esc(tr('exc_empty')) + '</p>';
}

/* ── ссылка, копирование, PDF ───────────────────────────────────────────── */

function saveUrl() {
    try {
        const u = new URL(location.href);
        ['code', 'g', 'okpd', 'a'].forEach((k) => u.searchParams.delete(k));
        if (S.kindView === 'code' && S.code) u.searchParams.set('code', S.code);
        if (S.kindView === 'group' && S.group) u.searchParams.set('g', S.group);
        if (S.okpd) u.searchParams.set('okpd', S.okpd);
        const a = Object.entries(S.answers).map(([k, v]) => k + ':' + (v === true ? '1' : v === false ? '0' : v)).join(',');
        if (a) u.searchParams.set('a', a);
        history.replaceState(history.state, '', u.toString());
    } catch (e) { /* old browser */ }
}

function summaryText() {
    const r = S.result;
    const lines = [$('mc-answer-l').textContent + ': ' + $('mc-answer-v').textContent, $('mc-answer-sub').textContent];
    for (const [, ls] of groupLines()) {
        const l = ls.find((x) => x.status === 'required') || ls[0];
        lines.push('• ' + l.group.name + ' — ' + (l.status === 'required' ? stageText(l.stage.stage) + ', ' + dmy(l.stage.since) : tr('st_' + l.status)) + '. ' + tr('basis', { rules: l.group.rules }));
    }
    lines.push(tr('asof_plain', { date: dmy(W.checked) }));
    lines.push(location.href);
    return lines.join('\n');
}

$('mc-copy').addEventListener('click', () => {
    const txt = summaryText();
    const done = () => { toast(tr('copied'), 'success'); feature('mc_copy', {}); };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(txt).then(done).catch(() => toast(tr('copy_failed'), 'warning'));
    else toast(tr('copy_failed'), 'warning');
});
$('mc-share').addEventListener('click', () => {
    const url = location.href;
    feature('mc_share', {});
    if (navigator.share) { navigator.share({ title: document.title, text: $('mc-answer-v').textContent, url }).catch(() => {}); return; }
    if (navigator.clipboard) navigator.clipboard.writeText(url).then(() => toast(tr('link_copied'), 'success')).catch(() => toast(tr('copy_failed'), 'warning'));
});

$('mc-pdf').addEventListener('click', () => {
    if (!window.__generatePDF) { toast(tr('pdf_not_ready'), 'warning'); return; }
    const box = document.createElement('div');
    box.className = 'mc-pdf';
    const r = S.result;
    const what = S.kindView === 'group' ? (M.groups.get(S.group) || {}).name : S.kindView === 'okpd' ? 'ОКПД 2 ' + S.okpd : T.fmtCode(S.code);
    let h = '<h1 style="font-size:22px;margin:0 0 6px">' + esc(tr('pdf_h', { what })) + '</h1>'
        + '<p style="font-size:20px;font-weight:800;margin:0 0 4px">' + esc($('mc-answer-v').textContent) + '</p>'
        + '<p style="font-size:13px;margin:0 0 12px">' + esc($('mc-answer-sub').textContent) + '</p>';
    if (S.lookup && S.lookup.entry && S.kindView === 'code') {
        h += '<h2 style="font-size:15px;margin:14px 0 6px">' + esc(tr('code_title')) + '</h2><table style="width:100%;border-collapse:collapse;font-size:12px">'
            + S.lookup.chain.map((e) => '<tr><td style="padding:3px 8px 3px 0;white-space:nowrap;vertical-align:top"><b>' + esc(T.fmtCode(e[0])) + '</b></td><td style="padding:3px 0">' + esc(e[2]) + '</td></tr>').join('') + '</table>';
    }
    for (const [, ls] of groupLines()) {
        const l = ls.find((x) => x.status === 'required') || ls[0];
        h += '<h2 style="font-size:15px;margin:14px 0 4px">' + esc(l.group.name) + ' — ' + esc(l.status === 'required' ? stageText(l.stage.stage) : tr('st_' + l.status)) + '</h2>'
            + '<p style="font-size:12px;margin:0 0 4px">' + esc(tr('basis', { rules: l.group.rules })) + '</p>';
        const items = ls.filter((x) => x.item).map((x) => '<li>' + esc(tr('item_line', { n: x.item.n, name: x.item.name, date: x.row ? dmy(x.row.date) : '' })) + (x.status !== 'required' ? ' — ' + esc(tr('st_' + x.status)) : '') + '</li>').join('');
        if (items) h += '<ul style="font-size:12px;margin:0 0 6px;padding-left:18px">' + items + '</ul>';
    }
    const ev = calEvents();
    if (ev.length) {
        h += '<h2 style="font-size:15px;margin:14px 0 6px">' + esc(tr('cal_title_pdf')) + '</h2><table style="width:100%;border-collapse:collapse;font-size:12px">'
            + ev.map((e) => '<tr><td style="padding:3px 8px 3px 0;white-space:nowrap"><b>' + esc(dmy(e.d)) + '</b></td><td style="padding:3px 0">' + esc(evText(e.t)) + (e.scope ? ': ' + esc(e.scope) : '') + '</td></tr>').join('') + '</table>';
    }
    const steps = planSteps(S.role);
    if (steps.length) {
        h += '<h2 style="font-size:15px;margin:14px 0 6px">' + esc(tr('plan_pdf', { role: (W.roles || {})[S.role] || '' })) + '</h2><ol style="font-size:12px;padding-left:18px;margin:0">'
            + steps.map((s) => '<li style="margin-bottom:4px">' + esc(s.t) + (s.d ? '<br><span style="color:#555">' + esc(s.d) + '</span>' : '') + '</li>').join('') + '</ol>';
    }
    h += '<p style="font-size:11px;color:#555;margin-top:14px">' + esc(tr('pdf_foot', { date: dmy(W.checked), url: location.href })) + '</p>'
        + '<p style="font-size:11px;color:#555">' + esc(document.querySelector('.tc-disclaimer') ? document.querySelector('.tc-disclaimer').textContent : '') + '</p>';
    box.innerHTML = h;
    feature('mc_pdf', { v: r.verdict });
    stage('exported', { what: 'pdf' });
    window.__generatePDF({ element: box, filename: 'marking-' + (S.code || S.group || 'check') + '.pdf', title: tr('pdf_title') })
        .catch((err) => { if (window.__handleError) window.__handleError(err, 'mc_pdf', { say: tr('pdf_failed') }); });
});

/* ── ОКПД 2 и дата ──────────────────────────────────────────────────────── */

$('mc-okpd').addEventListener('change', () => {
    S.okpd = $('mc-okpd').value.replace(/\s+/g, '');
    if (S.okpd && !/^\d{2}(\.\d{1,3}){0,4}$/.test(S.okpd)) { toast(tr('okpd_bad'), 'warning'); return; }
    if (S.kindView === 'code' || S.kindView === 'okpd') { if (!S.code && S.okpd) S.kindView = 'okpd'; compute(); saveUrl(); }
    feature('mc_okpd', { set: S.okpd ? 1 : 0 });
});
$('mc-date').addEventListener('change', () => {
    const v = $('mc-date').value;
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) { S.today = v; if (S.result) compute(); feature('mc_date', {}); }
});

/* ── штрихкод и код маркировки ──────────────────────────────────────────── */

function onProductCode(c, from) {
    const box = $('mc-scanned');
    const gtin = c.kind === 'mark' ? c.gtin : c.value;
    box.hidden = false;
    box.textContent = c.kind === 'mark' ? tr('mark_note', { gtin }) : tr('gtin_note', { gtin });
    feature('mc_product_code', { kind: c.kind, from });
    lookupGtin(gtin).then((found) => {
        if (found && found.failed) {
            // Сервер не ответил: это не «товара нет», а «не узнали» — поле ждёт название.
            box.textContent = tr('gtin_lookup_failed', { gtin });
            qEl.value = '';
            qEl.focus();
            diag('mc_gtin_lookup_failed', { via: found.via || '' });
            return;
        }
        if (found) {
            const name = found.name;
            // Поиск — по категории, если она узнана («кофе»), иначе по названию.
            const q = found.cat || name;
            box.textContent = (c.kind === 'mark' ? tr('mark_found', { name }) : tr('gtin_found', { name })) + (found.cat ? ' ' + tr('found_cat', { cat: found.cat }) : '');
            qEl.value = q;
            S.kind = 'text';
            loadTree().then(() => { suggest(q); qEl.focus(); }).catch(() => {});
            feature('mc_gtin_match', { cat: found.cat ? 1 : 0, via: found.via || '' });
        } else {
            box.textContent = tr('gtin_not_found', { gtin });
            // Цифры GTIN в поле ничего не найдут — поле ждёт название.
            qEl.value = '';
            qEl.focus();
            feature('mc_gtin_miss', {});
        }
    });
}

/* Открытые базы товаров — те же, что у сканера штрихкодов: по EAN-13 они знают
   название, по нему мы подбираем позицию. Группу по GTIN не определить честно. */
/* Категории открытых баз (теги en:…) → слово нашего словаря: по марке
   («Jacobs Monarch») позицию ТН ВЭД не найти, по категории («кофе») — да. */
const OFF_CATS = {
    'coffees': ['кофе', 'coffee'], 'instant-coffees': ['кофе растворимый', 'instant coffee'], 'teas': ['чай', 'tea'], 'cocoa-and-its-products': ['какао', 'cocoa'], 'chicory': ['цикорий', 'chicory'],
    'cheeses': ['сыр', 'cheese'], 'milks': ['молоко', 'milk'], 'yogurts': ['йогурт', 'yogurt'], 'kefirs': ['кефир', 'kefir'], 'sour-creams': ['сметана', 'sour cream'], 'cottage-cheeses': ['творог', 'cottage cheese'],
    'butters': ['сливочное масло', 'butter'], 'ice-creams': ['мороженое', 'ice cream'], 'chocolates': ['шоколад', 'chocolate'], 'candies': ['конфеты', 'candy'], 'confectioneries': ['конфеты', 'candy'],
    'biscuits': ['печенье', 'cookies'], 'wafers': ['вафли', 'wafers'], 'cakes': ['торт', 'cake'], 'marshmallows': ['зефир', 'marshmallow'], 'chewing-gum': ['жевательная резинка', 'chewing gum'],
    'fruit-juices': ['сок', 'juice'], 'juices': ['сок', 'juice'], 'nectars': ['нектар', 'nectar'], 'waters': ['вода питьевая', 'drinking water'], 'mineral-waters': ['минеральная вода', 'mineral water'],
    'sodas': ['газировка', 'soda'], 'energy-drinks': ['энергетик', 'energy drink'], 'beers': ['пиво', 'beer'], 'non-alcoholic-beers': ['безалкогольное пиво', 'non-alcoholic beer'], 'kvass': ['квас', 'kvass'],
    'pastas': ['макароны', 'pasta'], 'flours': ['мука', 'flour'], 'rices': ['рис', 'rice'], 'buckwheat': ['гречка', 'buckwheat'], 'cereals-and-their-products': ['крупа', 'groats'], 'breakfast-cereals': ['мюсли', 'muesli'],
    'honeys': ['мед', 'honey'], 'sauces': ['соус', 'sauce'], 'mayonnaises': ['майонез', 'mayonnaise'], 'ketchup': ['кетчуп', 'ketchup'], 'mustards': ['горчица', 'mustard'], 'spices': ['специи', 'spices'], 'vinegars': ['уксус', 'vinegar'],
    'chips-and-fries': ['чипсы', 'potato chips'], 'crisps': ['чипсы', 'crisps'], 'nuts': ['орешки', 'nuts'], 'vegetable-oils': ['растительное масло', 'vegetable oil'], 'sunflower-oils': ['подсолнечное масло', 'sunflower oil'],
    'olive-oils': ['оливковое масло', 'olive oil'], 'canned-fishes': ['консервы рыбные', 'canned fish'], 'canned-meats': ['тушенка', 'canned meat'], 'canned-vegetables': ['консервы овощные', 'canned vegetables'],
    'jams': ['варенье', 'jam'], 'sausages': ['колбаса', 'sausage'], 'cat-food': ['корм для кошек', 'cat food'], 'dog-food': ['корм для собак', 'dog food'], 'pet-food': ['корм для животных', 'pet food'],
    'dietary-supplements': ['бад', 'dietary supplement'], 'baby-foods': ['детское питание', ''], 'shampoos': ['шампунь', 'shampoo'], 'deodorants': ['дезодорант', 'deodorant'], 'toothpastes': ['зубная паста', 'toothpaste'],
    'soaps': ['мыло', 'soap'], 'shower-gels': ['гель для душа', 'shower gel'], 'creams': ['крем', 'cream'], 'perfumes': ['духи', 'perfume'], 'laundry-detergents': ['стиральный порошок', 'laundry detergent'],
};

/* Самая узкая из узнанных категорий (теги идут от общей к частной). Слово — на
   языке страницы: английские слова есть в словаре (`words_en`), и в поле
   английской страницы не появляется кириллица. */
function categoryWord(tags) {
    for (const t of (tags || []).slice().reverse()) {
        const k = String(t).replace(/^[a-z]{2}:/, '');
        const w = OFF_CATS[k] && OFF_CATS[k][IS_RU ? 0 : 1];
        if (w) return w;
    }
    return '';
}

function fetchWithin(url, ms, init) {
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), ms) : 0;
    return fetch(url, Object.assign({}, init || {}, ctl ? { signal: ctl.signal } : {})).finally(() => clearTimeout(timer));
}

/* Что за товар под штрихкодом — через НАШ сервер (/api/barcode/product: свой
   справочник и открытые базы за ним, одна дверь со сканерами). С русской
   страницы браузер чужие API не спрашивает; английская — если наш сервер не
   ответил, спрашивает открытые базы сама, как раньше.
   → {name, cat, via} | null (товара нет) | {failed: true} (не узнали). */
async function lookupGtin(gtin) {
    try {
        const r = await fetchWithin('/api/barcode/product?gtin=' + encodeURIComponent(gtin), 8000, { credentials: 'omit' });
        if (r.ok) {
            const j = await r.json();
            if (j && typeof j.found === 'boolean') {
                return j.found && j.name ? { name: String(j.name).slice(0, 80), cat: categoryWord(j.categories), via: 'own' } : null;
            }
        }
        if (r.status === 422) return null;   // не штрихкод товара — товара и нет
    } catch (e) { /* server unreachable — below */ }
    if (IS_RU) return { failed: true, via: 'own' };
    const ean = gtin.length === 14 && gtin[0] === '0' ? gtin.slice(1) : gtin;
    const srcs = ['https://world.openfoodfacts.org', 'https://world.openbeautyfacts.org', 'https://world.openproductsfacts.org'];
    for (const s of srcs) {
        try {
            // Своя граница: ответ открытой базы, повисший на минуту, держал бы
            // строку «ищем…» вечно.
            const ctl = typeof AbortController === 'function' ? new AbortController() : null;
            const timer = ctl ? setTimeout(() => ctl.abort(), 5000) : 0;
            const r = await fetch(s + '/api/v2/product/' + encodeURIComponent(ean) + '.json?fields=product_name,product_name_ru,generic_name_ru,generic_name,categories_tags', ctl ? { signal: ctl.signal } : {})
                .finally(() => clearTimeout(timer));
            if (!r.ok) continue;
            const j = await r.json();
            const p = j && j.status === 1 && j.product;
            const name = p && (p.product_name_ru || p.generic_name_ru || p.product_name || p.generic_name);
            if (name) return { name: String(name).slice(0, 80), cat: categoryWord(p.categories_tags), via: 'direct' };
        } catch (e) { /* next source */ }
    }
    return null;
}

/* ── камера и фото ──────────────────────────────────────────────────────── */

let cam = null, camLoop = 0, camOn = false;

async function startCam() {
    const box = $('mc-cam');
    if (!window.CameraManager || !window.__qrDecode || !window.__qrDecode.isSupported()) { toast(tr('cam_unsupported'), 'warning'); diag('mc_cam_failed', { code: 'unsupported' }); openPhoto(true); return; }
    box.hidden = false;
    $('mc-scan-btn').setAttribute('aria-expanded', 'true');
    $('mc-cam-say').textContent = tr('cam_starting');
    try { window.__qrDecode.ready().catch(() => {}); } catch (e) { /* engine loads later */ }
    try {
        if (!cam) cam = new window.CameraManager({ slug: SLUG });
        const sz = window.__qrDecode.liveCameraSize();
        const stream = await cam.start({ facingMode: 'environment', width: sz.width, height: sz.height });
        if (box.hidden || document.hidden) { cam.stop(); return; }
        camOn = true;
        $('mc-cam-say').textContent = tr('cam_hint');
        feature('mc_cam_started', {});
        stage('query', { kind: 'camera', via: 'camera' });
        // Кадры — через дверь (play без ожидания, сторож первого кадра, записка у кнопки).
        cam.show($('mc-video'), stream, { anchor: $('mc-scan-btn') }).then((ok) => { if (!ok && camOn) stopCam(); });
        loop();
    } catch (err) {
        box.hidden = true;
        $('mc-scan-btn').setAttribute('aria-expanded', 'false');
        const code = (err && err.code) || '';
        // Причину уже сказала записка двери у кнопки — тост только там, где её нет.
        if (!(cam && cam.hasHelp && cam.hasHelp())) toast(code === 'denied' ? tr('cam_denied') : code === 'not_found' ? tr('cam_missing') : tr('cam_failed'), 'warning');
        diag('mc_cam_failed', { code });
        openPhoto(true);
    }
}

function loop() {
    clearInterval(camLoop);
    let busy = false;
    const state = {};
    camLoop = setInterval(() => {
        if (!camOn || busy) return;
        const cut = window.__qrDecode.liveFrame($('mc-video'), { aim: $('mc-aim'), state });
        if (!cut) return;
        busy = true;
        window.__qrDecode.fromCanvasFast(cut.canvas).then((res) => {
            busy = false;
            if (!camOn || !res || !res.length) return;
            const hit = res[0];
            stopCam();
            if (navigator.vibrate) try { navigator.vibrate(40); } catch (e) { /* no vibration */ }
            onScanned(hit.text, 'camera');
        }).catch((err) => {
            busy = false;
            if (err && err.code === 'ENGINE') { stopCam(); toast(tr('cam_engine'), 'error'); }
        });
    }, 300);
}

function stopCam() {
    camOn = false;
    clearInterval(camLoop);
    try { if (cam) cam.stop(); } catch (e) { /* already stopped */ }
    try { $('mc-video').srcObject = null; } catch (e) { /* detached */ }
    $('mc-cam').hidden = true;
    $('mc-scan-btn').setAttribute('aria-expanded', 'false');
}

function onScanned(text, via) {
    // Восемь цифр с камеры или фото — EAN-8, а не код ТН ВЭД, как у набранных.
    const c = E.classifyQuery(text, { scanned: true });
    feature('mc_scanned', { via, kind: c.kind });
    if (c.kind === 'gtin' || c.kind === 'mark') { qEl.value = c.kind === 'mark' ? c.gtin : c.value; onProductCode(c, via); return; }
    if (c.kind === 'tnved') { qEl.value = c.value; setCode(c.value, { from: via }); return; }
    // Табачная пачка (29 знаков без AI) и прочее: показываем как есть.
    const box = $('mc-scanned');
    box.hidden = false;
    box.textContent = tr('scan_other', { text: String(text).slice(0, 60) });
}

/* force=true — открыть (запасной путь после отказа камеры), иначе переключить.
   Переключение здесь закрывало уже открытую зону фото как раз тогда, когда она нужна. */
function openPhoto(force) {
    const p = $('mc-photo');
    p.hidden = force === true ? false : !p.hidden;
    $('mc-photo-btn').setAttribute('aria-expanded', p.hidden ? 'false' : 'true');
}

$('mc-scan-btn').addEventListener('click', () => { if (camOn || !$('mc-cam').hidden) stopCam(); else startCam(); });
$('mc-cam-stop').addEventListener('click', stopCam);
$('mc-photo-btn').addEventListener('click', () => openPhoto());
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden' && camOn) stopCam(); });
window.addEventListener('pagehide', () => { if (camOn) stopCam(); });

(function bindUpload() {
    const zone = $('mc-upload');
    if (!zone) return;
    /* Только то, что бывает на товаре: код маркировки, штрихкоды товара, QR и Code 128.
       Без фильтра лестница декодера на снимке без кода шла 27 с (12 Мп, все форматы). */
    const FORMATS = ['DataMatrix', 'EAN13', 'EAN8', 'UPCA', 'UPCE', 'Code128', 'QRCode'];
    const onFile = (e) => {
        const file = e.detail && e.detail.file;
        if (!file || !window.__qrDecode) return;
        const box = $('mc-scanned'), t0 = Date.now();
        const heic = !!(window.HeicDecode && window.HeicDecode.isHeic(file));
        box.hidden = false;
        box.textContent = tr('photo_reading');
        stage('query', { kind: 'photo', via: 'photo' });
        const prep = heic
            ? window.HeicDecode.toBlob(file, 'image/jpeg', 0.92).then((b) => new File([b], 'photo.jpg', { type: 'image/jpeg' })).catch(() => file)
            : Promise.resolve(file);
        prep.then((f) => window.__qrDecode.fromFile(f, { formats: FORMATS })).then((res) => {
            const ms = Date.now() - t0;
            if (res && res.length) { onScanned(res[0].text, 'photo'); feature('mc_photo_ok', { n: res.length, ms, heic: heic ? 1 : 0, fmt: String(res[0].format || '').slice(0, 16) }); }
            else {
                // Совет — у зоны фото, где человек и смотрит; тост повторил бы его вторым голосом.
                box.textContent = tr('photo_none');
                diag('mc_photo_none', { ms, heic: heic ? 1 : 0 });
            }
        }).catch((err) => {
            box.hidden = true;
            diag('mc_photo_failed', { heic: heic ? 1 : 0, code: String((err && err.code) || '').slice(0, 20) });
            if (window.__handleError) window.__handleError(err, 'mc_photo_decode', { say: tr('photo_failed') });
        });
    };
    if (window.__bindUploaderFile) window.__bindUploaderFile(zone, (file) => onFile({ detail: { file } }));
    else zone.addEventListener('tool-uploader:file', onFile);
})();

/* ── список кодов ───────────────────────────────────────────────────────── */

/* Из каждой строки — первая ячейка, похожая на код ТН ВЭД (4–10 цифр; пробелы и
   точки внутри кода снимаются: «6109 10 000 0»). Строка с артикулом и названием
   («Футболка;6109100000;шт») тоже годится; штрихкод (12–14 цифр) — не код. */
const BATCH_MAX = 500;
let batchRows = [];
function batchCodes(text) {
    const out = [], seen = new Set();
    let skipped = 0;
    for (const line of String(text || '').split(/\r?\n/)) {
        if (!line.trim()) continue;
        let code = '';
        for (const cell of line.split(/[;,\t|]/)) {
            const d = cell.replace(/[\s.\u00a0]/g, '');
            if (/^\d{4,10}$/.test(d)) { code = d; break; }
        }
        if (!code) { skipped++; continue; }
        if (seen.has(code)) continue;
        seen.add(code);
        out.push(code);
    }
    return { codes: out, skipped };
}

function batchVerdict(ev) {
    const req = ev.lines.filter((l) => l.status === 'required');
    const first = req[0] || ev.lines[0] || null;
    const v = ev.verdict;
    const label = v === 'yes' || v === 'yes_multi' ? tr('menu_marked')
        : v === 'soon' ? tr('menu_soon', { date: dmy(first && first.stage.since) })
        : v === 'experiment' ? tr('menu_exp') : v === 'depends' ? tr('menu_part') : tr('menu_no');
    const cls = v === 'yes' || v === 'yes_multi' ? 'mandatory' : v === 'soon' ? 'upcoming' : v === 'experiment' || v === 'depends' ? 'experiment' : 'ok';
    return { v, label, cls, group: first && req.length ? first.group.name : '', since: first && req.length ? first.stage.since : '', rules: first && req.length ? first.group.rules : '' };
}

function runBatch() {
    const { codes, skipped } = batchCodes($('mc-batch-in').value);
    const sum = $('mc-batch-sum');
    if (!codes.length) { sum.textContent = tr('batch_none'); $('mc-batch-wrap').hidden = true; $('mc-batch-csv').hidden = true; return; }
    const list = codes.slice(0, BATCH_MAX);
    sum.textContent = tr('loading_tree');
    const done = () => {
        batchRows = list.map((code) => {
            const ev = E.evaluate(M, E.matchTnved(M, code), { today: S.today, code });
            const L0 = IDX ? T.lookup(IDX, code) : null;
            return Object.assign({ code, name: L0 && L0.entry ? L0.entry[2] : '' }, batchVerdict(ev));
        });
        const n = { yes: 0, soon: 0, part: 0, no: 0 };
        batchRows.forEach((r) => {
            if (r.cls === 'mandatory') n.yes++; else if (r.cls === 'upcoming') n.soon++; else if (r.cls === 'experiment') n.part++; else n.no++;
        });
        sum.textContent = tr('batch_sum', { n: batchRows.length, yes: n.yes, soon: n.soon, part: n.part, no: n.no })
            + (codes.length > BATCH_MAX ? ' ' + tr('batch_more', { n: BATCH_MAX }) : '')
            + (skipped ? ' ' + tr('batch_skipped', { n: skipped }) : '');
        $('mc-batch-sub').textContent = '· ' + batchRows.length;
        $('mc-batch-table').innerHTML = '<thead><tr><th>' + esc(tr('batch_h_code')) + '</th><th>' + esc(tr('batch_h_name')) + '</th><th>' + esc(tr('batch_h_v')) + '</th><th>' + esc(tr('batch_h_group')) + '</th></tr></thead><tbody>'
            + batchRows.map((r) => '<tr><td><button type="button" class="mc-child" data-batch-code="' + r.code + '"><span class="mc-c">' + esc(T.fmtCode(r.code)) + '</span></button></td>'
                + '<td>' + esc(r.name || '—') + '</td>'
                + '<td><span class="mc-badge mc-b-' + r.cls + '">' + esc(r.label) + '</span></td>'
                + '<td>' + esc(r.group || '—') + (r.since ? '<span class="mc-sub"> ' + esc(dmy(r.since)) + '</span>' : '') + '</td></tr>').join('')
            + '</tbody>';
        $('mc-batch-wrap').hidden = false;
        $('mc-batch-csv').hidden = false;
        feature('mc_batch', { n: batchRows.length, yes: n.yes, soon: n.soon, part: n.part, no: n.no, skipped, over: codes.length > BATCH_MAX ? 1 : 0 });
        stage('query', { kind: 'batch', via: 'batch' });
        try { if (window.__aeToolComplete) window.__aeToolComplete({ v: 'batch', n: batchRows.length }); } catch (e) { /* optional */ }
    };
    loadTree().then(done, done);
}

function batchCsv() {
    if (!batchRows.length) return;
    const q = (v) => {
        let s = String(v == null ? '' : v);
        if (/^[=+\-@]/.test(s)) s = "'" + s;   // не формула в Excel
        return '"' + s.replace(/"/g, '""') + '"';
    };
    const head = [tr('batch_h_code'), tr('batch_h_name'), tr('batch_h_v'), tr('batch_h_group'), tr('batch_h_since'), tr('batch_h_rules')];
    const lines = [head.map(q).join(';')].concat(batchRows.map((r) => [T.fmtCode(r.code), r.name, r.label, r.group, r.since ? dmy(r.since) : '', r.rules].map(q).join(';')));
    lines.push(q(tr('asof_plain', { date: dmy(W.checked) })));
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    if (window.__downloadFile) window.__downloadFile(blob, 'marking-list-' + batchRows.length + '.csv');
    feature('mc_batch_csv', { n: batchRows.length });
    stage('exported', { what: 'batch_csv' });
}

$('mc-batch-go').addEventListener('click', runBatch);
$('mc-batch-csv').addEventListener('click', batchCsv);
$('mc-batch-table').addEventListener('click', (e) => {
    const b = e.target.closest('[data-batch-code]');
    if (!b) return;
    const code = b.getAttribute('data-batch-code');
    qEl.value = T.fmtCode(code);
    setCode(code, { from: 'batch' });
    const panel = $('mc-answer-panel');
    if (panel && panel.scrollIntoView) panel.scrollIntoView({ block: 'nearest' });
});
$('mc-batch-open').addEventListener('click', () => {
    const d = $('mc-batch');
    d.open = true;
    if (d.scrollIntoView) d.scrollIntoView({ block: 'nearest' });
    try { $('mc-batch-in').focus({ preventScroll: true }); } catch (e) { /* old engine */ }
    loadTree().catch(() => {});
    feature('mc_batch_open', {});
});

/* ── таблица групп под прибором ─────────────────────────────────────────── */

document.addEventListener('click', (e) => {
    const b = e.target.closest('.mc-gl[data-group]');
    if (b) showGroup(b.dataset.group, 'table');
});

/* ── старт ──────────────────────────────────────────────────────────────── */

(function init() {
    try {
        const saved = window.__toolSettings ? window.__toolSettings.load(SLUG, 'role', '') : '';
        if (saved && (W.roles || {})[saved]) {
            S.role = saved;
            [...$('mc-roles').children].forEach((x) => { const on = x.dataset.role === saved; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        }
    } catch (e) { /* storage off */ }
    const u = new URLSearchParams(location.search);
    const a = u.get('a');
    const ans = {};
    if (a) for (const kv of a.split(',')) { const [k, v] = kv.split(':'); if (M.questions[k]) ans[k] = v === '1' ? true : v === '0' ? false : v; }
    if (u.get('okpd')) { S.okpd = u.get('okpd'); $('mc-okpd').value = S.okpd; }
    const code = E.digits(u.get('code') || '');
    const q = u.get('q');
    if (code.length >= 4) {
        qEl.value = T.fmtCode(code);
        setCode(code, { from: 'url' });
        if (a) { S.answers = ans; compute(); }
    } else if (u.get('g') && M.groups.has(u.get('g'))) {
        showGroup(u.get('g'), 'url');
        if (a) { S.answers = ans; compute(); }
    } else if (/^\d{8,14}$/.test(E.digits(u.get('gtin') || ''))) {
        // Из карточки кода маркировки в сканерах: по GTIN ищем название товара.
        const g = E.digits(u.get('gtin'));
        qEl.value = g;
        onProductCode({ kind: 'mark', value: g, gtin: g }, 'scanner');
    } else if (S.okpd) {
        setOkpdQuery(S.okpd);
    } else if (q) {
        qEl.value = q;
        loadTree().then(() => suggest(q)).catch(() => {});
    } else {
        renderPlan();
    }
    feature('mc_ready', {});
    stage('ready');
    // Справочник ТН ВЭД (≈0,4 МБ сжатым) — заранее, когда страница уже
    // показана и сеть свободна, чтобы первая подсказка не ждала загрузки.
    // Не на экономном трафике.
    try {
        const save = navigator.connection && navigator.connection.saveData;
        if (!save && !IDX) {
            const go = () => { if (document.visibilityState === 'visible') loadTree().catch(() => {}); };
            if (window.requestIdleCallback) setTimeout(() => window.requestIdleCallback(go, { timeout: 4000 }), 2500);
            else setTimeout(go, 3000);
        }
    } catch (e) { /* optional */ }
})();
